/**
 * Download Flow Entity Modal Component
 * Modal for downloading flow entities (processes, workflows) with format selection and preview
 */

import React, { useState, useEffect } from 'react';
import { Modal } from '../Support/Modal';
import { useUIStore } from '@flow-studio/store';
import { getPreviewFlowEntity, downloadFlowEntity } from '../../utils/flowEntityDownload';

export interface DownloadFlowEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'process' | 'app' | 'processthread' | 'processelement';
  entityId: number;
  entityName: string;
  apiBaseUrl: string;
}

export const DownloadFlowEntityModal: React.FC<DownloadFlowEntityModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName,
  apiBaseUrl,
}) => {
  const { theme, showToast } = useUIStore();
  const [selectedFormat, setSelectedFormat] = useState<'json' | 'html'>('json');
  const [previewContent, setPreviewContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch preview when modal opens or format changes
  useEffect(() => {
    if (!isOpen) return;

    const fetchPreview = async () => {
      setIsLoading(true);
      setError(null);

      const result = await getPreviewFlowEntity({
        entityType,
        entityRecordID: entityId,
        format: selectedFormat,
        apiBaseUrl,
      });

      if (result.error) {
        setError(result.error);
        setPreviewContent('');
        showToast('error', `Preview failed: ${result.error}`);
      } else {
        setPreviewContent(result.content);
      }

      setIsLoading(false);
    };

    fetchPreview();
  }, [isOpen, selectedFormat, entityType, entityId, apiBaseUrl, showToast]);

  const handleDownload = async () => {
    const result = await downloadFlowEntity({
      entityType,
      entityRecordID: entityId,
      format: selectedFormat,
      apiBaseUrl,
    });

    if (result.error) {
      setError(result.error);
      showToast('error', `Download failed: ${result.error}`);
    } else {
      showToast('success', `${entityType} downloaded successfully`);
    }
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s',
  };

  const selectedButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: theme === 'dark' ? '#3a8c45' : '#10b981',
    color: '#fff',
    borderColor: theme === 'dark' ? '#3a8c45' : '#10b981',
  };

  const unselectedButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: 'transparent',
    color: theme === 'dark' ? '#cbd5e1' : '#475569',
    borderColor: theme === 'dark' ? '#475569' : '#cbd5e1',
  };

  const downloadButtonStyle: React.CSSProperties = {
    padding: '8px 20px',
    borderRadius: '6px',
    border: 'none',
    background: theme === 'dark' ? '#3a8c45' : '#10b981',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  };

  const contentAreaStyle: React.CSSProperties = {
    padding: '16px',
    background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
    border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'}`,
    borderRadius: '8px',
    maxHeight: '400px',
    overflowY: 'auto',
    fontFamily: 'monospace',
    fontSize: '12px',
    color: theme === 'dark' ? '#cbd5e1' : '#475569',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  const loadingStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    color: theme === 'dark' ? '#cbd5e1' : '#475569',
  };

  const errorStyle: React.CSSProperties = {
    color: '#ef4444',
    padding: '12px',
    background: theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
    borderRadius: '6px',
    marginBottom: '12px',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Download ${entityName}`}
      maxWidth="800px"
    >
      {/* Format Selection Buttons */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '12px' }}>
        <button
          onClick={() => setSelectedFormat('json')}
          style={selectedFormat === 'json' ? selectedButtonStyle : unselectedButtonStyle}
        >
          JSON
        </button>
        <button
          onClick={() => setSelectedFormat('html')}
          style={selectedFormat === 'html' ? selectedButtonStyle : unselectedButtonStyle}
        >
          HTML
        </button>
        <div style={{ flex: 1 }} />
        <button
          onClick={handleDownload}
          disabled={isLoading}
          style={{
            ...downloadButtonStyle,
            opacity: isLoading ? 0.6 : 1,
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download
        </button>
      </div>

      {/* Error Display */}
      {error && <div style={errorStyle}>{error}</div>}

      {/* Content Preview */}
      <div style={contentAreaStyle}>
        {isLoading ? (
          <div style={loadingStyle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            Loading preview...
          </div>
        ) : previewContent ? (
          previewContent.length > 500 ? (
            <>
              {previewContent.substring(0, 500)}
              ...
              <br />
              <br />
              <em>(Preview truncated, download to see full content)</em>
            </>
          ) : (
            previewContent
          )
        ) : (
          <em>No content to display</em>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
};
